import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router-dom';
import LoadingBar from 'react-top-loading-bar';

import Toast from '@components/Toast/Toast';
import { IRoute } from '@interfaces/routes';
import privateRoutes from '@routes/private';
import publicRoutes from '@routes/public';
import urls from '@routes/urls';
import { handleRefreshToken } from '@store/features/auth/token/actions';
import { authTokenSelector } from '@store/features/auth/token/selectors';
import { updateTokens } from '@store/features/auth/token/slice';
import { handleGetUserProfile } from '@store/features/auth/user/actions';
import { userDataSelector } from '@store/features/auth/user/selectors';
import { handleGetDashboardOrgInfo } from '@store/features/dashboardOrgInfo/actions';
import { loadingCountSelector } from '@store/features/loading/selectors';
import useDispatch from '@store/hooks/useDispatch';
import { colors } from '@utils/constants';
import { getAuthTokenFromLocalStorage, getRefreshTokenFromLocalStorage } from '@utils/token';

import Footer from './Footer/Footer';
import Header from './Header/Header';
import ProtectedRoute from './ProtectedRoute/ProtectedRoute';

const Root: FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(userDataSelector);
  const loading = useSelector(loadingCountSelector);
  const storageAuthToken = useSelector(authTokenSelector);

  const authToken = getAuthTokenFromLocalStorage();
  const refreshToken = getRefreshTokenFromLocalStorage();

  const location = useLocation();

  const [isTokenRefreshed, setIsTokenRefreshed] = useState<boolean>(false);

  useEffect(() => {
    if (authToken && refreshToken) {
      dispatch(
        updateTokens({
          authToken,
          refreshToken,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (storageAuthToken) {
      dispatch(handleGetUserProfile());
    }
  }, [dispatch, storageAuthToken]);

  useEffect(() => {
    if (user && !user.organizationId && !isTokenRefreshed) {
      dispatch(handleRefreshToken());
      setIsTokenRefreshed(true);
    }

    if (user) {
      dispatch(handleGetDashboardOrgInfo());
    }
  }, [dispatch, user]);

  const loadingRef = useRef(null);

  useEffect(() => {
    if (loadingRef?.current) {
      loading > 0
        ? // @ts-ignore
          loadingRef.current.continuousStart()
        : // @ts-ignore
          loadingRef.current.complete();
    }
  }, [loading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const rootDiv = document.getElementById('root');
    if (rootDiv) {
      rootDiv.style.minHeight = `${window.innerHeight}px`;
    }
  }, []);

  return (
    <>
      <LoadingBar color={colors.primary} ref={loadingRef} />

      <Toast />

      {location.pathname !== urls.login && <Header />}

      <Routes>
        {privateRoutes.map((route: IRoute, i: number) => (
          <Route
            key={i}
            path={route.path}
            element={
              <ProtectedRoute admin={route.admin}>
                <route.component />
              </ProtectedRoute>
            }
          />
        ))}

        {publicRoutes.map((route: IRoute, i: number) => (
          <Route key={i} path={route.path} element={<route.component />} />
        ))}
      </Routes>

      <Footer />
    </>
  );
};

export default Root;
